import { createSlice } from '@reduxjs/toolkit';
import { BASE_END_POINT_NOTIFICATION, NOTIFICATION_END_POINT } from '../../../config';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';

const initialState = {
  current: null,
  list: [],
  unreadCount: 0,
  loading: false,
  error: null
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    fetchNotificationsRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getNotificationsByCurrentUserSucceeded(state, action) {
      state.list = action.payload.notifications;
      // Calculate unread count
      state.unreadCount = action.payload.notifications.filter(n => !n.read).length;
      state.loading = false;
      state.error = null;
    },
    fetchNotificationsFailure(state, action) {
      state.loading = false;
      state.error = action.payload.error;
    },
    currentUpdated(state, action) {
      state.current = action.payload.notification;
    },
    notificationMarkedAsRead(state, action) {
      const notificationId = action.payload.notificationId;
      const notification = state.list.find(n => n.id === notificationId);
      if (notification) {
        notification.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    notificationDeleted(state, action) {
      const notificationId = action.payload.notificationId;
      const notification = state.list.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
      state.list = state.list.filter(n => n.id !== notificationId);
    },
    newNotificationReceived(state, action) {
      // Check if notification already exists (prevent duplicates)
      const exists = state.list.some(n => n.id === action.payload.notification.id);
      if (!exists) {
        // Add new notification to the beginning of the list
        state.list.unshift(action.payload.notification);
        if (!action.payload.notification.read) {
          state.unreadCount += 1;
        }
      }
    }
  }
});

export const currentNotificationSelector = (state) =>
  state.notifications.current;
export const notificationsListSelector = (state) => state.notifications.list;
export const unreadNotificationsCountSelector = (state) => 
  state.notifications.unreadCount;
export const notificationsLoadingSelector = (state) => state.notifications.loading;

export const { 
  fetchNotificationsRequest,
  getNotificationsByCurrentUserSucceeded,
  fetchNotificationsFailure,
  currentUpdated,
  notificationMarkedAsRead,
  notificationDeleted,
  newNotificationReceived
} = notificationsSlice.actions;

// Thunk action to fetch notifications by user ID
export const fetchNotificationsByUserId = 
  ({ userId }) =>
  async (dispatch) => {
    console.log('🔍 [Notifications Thunk] Fetching notifications for userId:', userId);
    
    dispatch(fetchNotificationsRequest());
    
    try {
      const localAuth = getLocalStorageAuthentication();
      const userIdEncoded = encodeURIComponent(userId);
      const url = `${BASE_END_POINT_NOTIFICATION}${NOTIFICATION_END_POINT}/user/${userIdEncoded}`;
      
      console.log('🌐 [Notifications Thunk] Fetching from:', url);
      console.log('🔑 [Notifications Thunk] Using JWT token:', localAuth.token ? 'Present' : 'Missing');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // No notifications is not an error
          console.log('ℹ️ [Notifications Thunk] No notifications found (404)');
          dispatch(getNotificationsByCurrentUserSucceeded({ notifications: [] }));
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const notifications = data || [];
      
      console.log('✅ [Notifications Thunk] Received', notifications.length, 'notifications');
      
      dispatch(getNotificationsByCurrentUserSucceeded({ notifications }));
    } catch (error) {
      console.error('❌ [Notifications Thunk] Failed to fetch notifications:', error);
      dispatch(fetchNotificationsFailure({ error: error.message }));
    }
  };

// Thunk action to mark notification as read
export const markNotificationAsRead =
  ({ notificationId, notification }) =>
  async (dispatch, getState) => {
    console.log('📖 [Notifications Thunk] Marking notification as read:', notificationId);
    
    try {
      const localAuth = getLocalStorageAuthentication();
      
      // Get the notification from Redux state if not provided
      let notificationToUpdate = notification;
      if (!notificationToUpdate) {
        const state = getState();
        notificationToUpdate = state.notifications.list.find(n => n.id === notificationId);
        
        if (!notificationToUpdate) {
          throw new Error(`Notification ${notificationId} not found in state`);
        }
      }
      
      // Prepare the update payload - we need to send the complete notification with read=true
      // NOTE: Do NOT include created_at or updated_at - these are managed by the server
      const updatePayload = {
        id: notificationToUpdate.id,
        topic: notificationToUpdate.topic,
        body: notificationToUpdate.body,
        read: true, // Mark as read
        user_id: notificationToUpdate.user_id || notificationToUpdate.userId,
        user_email: notificationToUpdate.user_email || notificationToUpdate.userEmail || '',
        full_name: notificationToUpdate.full_name || notificationToUpdate.fullName || '',
        notification_type: notificationToUpdate.notification_type || notificationToUpdate.notificationType || 'general'
        // created_at and updated_at are intentionally omitted - server manages these
      };
      
      const url = `${BASE_END_POINT_NOTIFICATION}${NOTIFICATION_END_POINT}`;
      
      console.log('🌐 [Notifications Thunk] PUT request to:', url);
      console.log('📦 [Notifications Thunk] Payload:', updatePayload);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify(updatePayload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Notifications Thunk] Server error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ [Notifications Thunk] Notification marked as read:', data);
      
      // Update Redux state
      dispatch(notificationMarkedAsRead({ notificationId }));
      
      return { success: true };
    } catch (error) {
      console.error('❌ [Notifications Thunk] Failed to mark notification as read:', error);
      throw error;
    }
  };

// Thunk action to delete notification
export const deleteNotificationById =
  ({ notificationId }) =>
  async (dispatch) => {
    console.log('🗑️ [Notifications Thunk] Deleting notification:', notificationId);
    
    try {
      const localAuth = getLocalStorageAuthentication();
      const notificationIdEncoded = encodeURIComponent(notificationId);
      const url = `${BASE_END_POINT_NOTIFICATION}${NOTIFICATION_END_POINT}/delete/${notificationIdEncoded}`;
      
      console.log('🌐 [Notifications Thunk] GET request to:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      console.log('✅ [Notifications Thunk] Notification deleted');
      
      // Update Redux state
      dispatch(notificationDeleted({ notificationId }));
      
      dispatch(
        messageUpdated({
          text: 'Notification deleted successfully',
          severity: Severity.SUCCESS
        })
      );
      
      return { success: true };
    } catch (error) {
      console.error('❌ [Notifications Thunk] Failed to delete notification:', error);
      
      dispatch(
        messageUpdated({
          text: 'Failed to delete notification',
          severity: Severity.ERROR
        })
      );
      
      throw error;
    }
  };

export default notificationsSlice.reducer;
